import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to detect page visibility state
 * Used to pause/resume expensive operations when tab is hidden
 */
export const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [hiddenSince, setHiddenSince] = useState<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const nowVisible = !document.hidden;
      setIsVisible(nowVisible);
      
      if (!nowVisible) {
        setHiddenSince(Date.now());
      } else {
        setHiddenSince(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Helper to check if page has been hidden for a certain duration
  const hasBeenHiddenFor = useCallback((ms: number) => {
    if (hiddenSince === null) return false;
    return Date.now() - hiddenSince >= ms;
  }, [hiddenSince]);

  return { isVisible, hiddenSince, hasBeenHiddenFor };
};

/**
 * Returns a refetchInterval that's active when visible, disabled when hidden
 */
export const useAdaptiveRefetchInterval = (
  intervalWhenVisible: number | false,
  intervalWhenHidden: number | false = false
) => {
  const { isVisible } = usePageVisibility();
  return isVisible ? intervalWhenVisible : intervalWhenHidden;
};
